import { SyntheticEvent, useState } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import InputAdornment from '@mui/material/InputAdornment';

import CalendarIcon from '@images/svg-icons/calendarIcon.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';

import { PersonalDetailsFormStatus, ThirdFormFields } from '@interfaces/myProfileInterfaces';

import { capitalizeFirstLetter, getLabelFallback } from '@utils/utils';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';
import { getStringFromDate } from '@utils/dateUtils';

import { isValid, differenceInYears, parse, compareAsc } from 'date-fns';
import { getFormattedDate } from '@components/eye-exams/EyeExamsOverview';

const PersonalDetailsThirdForm = (): JSX.Element => {
    const {
        register,
        control,
        watch,
        formState: { errors, isSubmitting },
        setError,
        setValue,
        clearErrors,
    } = useFormContext();

    const watchDob = watch('dob');

    const initialFormData: Partial<PersonalDetailsFormStatus<ThirdFormFields>> = {
        dob: {
            success: watchDob ? true : false,
            focus: false,
        },
    };

    const [isOpenDatePicker, setIsOpenDatePicker] = useState<boolean>(false);
    const [formStatus, setFormStatus] =
        useState<Partial<PersonalDetailsFormStatus<ThirdFormFields>>>(initialFormData);

    /** The function below handles the onBlur, onChange e onFocus events for the form validation */

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;
        console.log('handleInputeEvent third form', errors, event.value, e);
        !isSubmitting && clearErrors(event.name);
        if (event.name === 'dob') {
            if (e.type === 'blur' || (isSubmitting && e.type === 'focus')) {
                const parsedDate = event.value && parse(event.value, 'P', new Date());
                return event.value
                    ? validateDate(parsedDate, event.value)
                    : (setError('dob', {
                          type: 'required',
                          message: replaceLabelPlaceholder(
                              'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                              '{FORM_FIELD}',
                              getLabelFallback(
                                  'MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_BIRTHDATE_LABEL'
                              )
                          ),
                      }),
                      setFormStatus({ ...formStatus, dob: { focus: false, success: false } }));
            }
        }
        !isSubmitting && setFormStatus({ ...formStatus, dob: { focus: true, success: false } });
    };

    /** The belows function checks if the input date is valid */

    const validateDate = (inputDate: Date, formattedDate: string) => {
        console.log(inputDate, formattedDate);
        isValid(inputDate) &&
        compareAsc(inputDate, new Date('01/01/1900')) !== -1 &&
        compareAsc(inputDate, new Date()) !== 1 &&
        differenceInYears(new Date(), inputDate) >= 18
            ? (clearErrors('dob'),
              console.log('Data valida'),
              setValue('dob', formattedDate),
              setFormStatus({ ...formStatus, dob: { focus: false, success: true } }))
            : (setError('dob', {
                  type: 'valueAsDate',
                  message:
                      differenceInYears(new Date(), inputDate) < 18
                          ? getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_OF_BIRTH_MESSAGE'
                            )
                          : getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_MESSAGE'),
              }),
              console.log('Data non valida'),
              setValue('dob', formattedDate),
              setFormStatus({ ...formStatus, dob: { focus: false, success: false } }));
    };

    return (
        <div className="third-form">
            <div className="dob">
                <span className="secondary-message large bold align-left">
                    {getLabelFallback('MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_BIRTHDATE')}
                </span>
                <div className="form-group">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                            {...register('dob')}
                            minDate={new Date('01/01/1900')}
                            disableFuture
                            label={capitalizeFirstLetter(
                                getLabelFallback(
                                    'MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_BIRTHDATE_LABEL'
                                )
                            )}
                            open={isOpenDatePicker}
                            onClose={() => setIsOpenDatePicker(false)}
                            onAccept={date => {
                                validateDate(date, getStringFromDate(date, 'MM/dd/yyyy'));
                            }}
                            openTo="year"
                            views={['year', 'month', 'day']}
                            inputFormat="MM/dd/yyyy"
                            value={watchDob}
                            onChange={() => {}}
                            renderInput={params => (
                                <TextField
                                    className={`${
                                        errors?.dob?.message
                                            ? 'form-field-error'
                                            : formStatus.dob.success
                                            ? 'form-field-success'
                                            : ''
                                    }`}
                                    helperText={errors?.dob?.message}
                                    name="dob"
                                    variant="outlined"
                                    onBlur={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onChange={(e: SyntheticEvent) => (
                                        clearErrors('dob'),
                                        setFormStatus({
                                            ...formStatus,
                                            dob: { success: false, focus: true },
                                        }),
                                        /^[\/0-9\b]+$/.test((e.target as HTMLInputElement).value)
                                            ? setValue(
                                                  'dob',
                                                  getFormattedDate(
                                                      (e.target as HTMLInputElement).value
                                                  )
                                              )
                                            : setValue('dob', '')
                                    )}
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        value: watchDob,
                                    }}
                                    InputProps={{
                                        endAdornment:
                                            formStatus.dob.focus && watchDob ? (
                                                <InputAdornment className="large" position="end">
                                                    <CloseIcon
                                                        className="cursor-pointer"
                                                        onMouseDown={() => setValue('dob', '')}
                                                    />
                                                    <CalendarIcon
                                                        className="input-field-icon"
                                                        onClick={() => setIsOpenDatePicker(true)}
                                                    />
                                                </InputAdornment>
                                            ) : errors?.dob?.message ? (
                                                <InputAdornment className="large" position="end">
                                                    <ErrorIcon />
                                                    <CalendarIcon
                                                        className="input-field-icon"
                                                        onClick={() => setIsOpenDatePicker(true)}
                                                    />
                                                </InputAdornment>
                                            ) : formStatus.dob.success ? (
                                                <InputAdornment className="large" position="end">
                                                    <SuccessIcon />
                                                    <CalendarIcon
                                                        className="input-field-icon"
                                                        onClick={() => setIsOpenDatePicker(true)}
                                                    />
                                                </InputAdornment>
                                            ) : (
                                                <CalendarIcon
                                                    className="input-field-icon medium"
                                                    onClick={() => setIsOpenDatePicker(true)}
                                                />
                                            ),
                                    }}
                                    error={Boolean(errors?.dob?.message)}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </div>
            </div>
            <div className="gender">
                <FormControl>
                    <FormLabel
                        id="gender-label"
                        className="secondary-message large bold align-left mb-16"
                    >
                        {getLabelFallback('MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_GENDER')}
                    </FormLabel>
                    <Controller
                        control={control}
                        name="gender"
                        defaultValue={''}
                        render={({ field }) => (
                            <RadioGroup aria-labelledby="gender-label" {...field}>
                                <FormControlLabel
                                    value="M"
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: 'black',
                                                '&.Mui-checked': {
                                                    color: 'black',
                                                },
                                            }}
                                        />
                                    }
                                    label={getLabelFallback(
                                        'MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_MALE'
                                    )}
                                />
                                <FormControlLabel
                                    value="F"
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: 'black',
                                                '&.Mui-checked': {
                                                    color: 'black',
                                                },
                                            }}
                                        />
                                    }
                                    label={getLabelFallback(
                                        'MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_FEMALE'
                                    )}
                                />
                            </RadioGroup>
                        )}
                    />
                </FormControl>
            </div>
        </div>
    );
};
export default PersonalDetailsThirdForm;
